import { AlertTriangle, Zap } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { usePlanStore } from '@/stores';
import { trackUpgradeCta } from '@/lib/upgradeTracking';

interface SidebarPlanWidgetProps {
  collapsed: boolean;
  /** Navigate to the billing page (plan badge / banner CTA). */
  onNavigateBilling: () => void;
}

/**
 * Sidebar plan badge + mini AI-credit usage meter + an 80% warning banner
 * (LIL-341). Reads the hydrated plan store; renders nothing until subscription
 * data is available so it never flashes a placeholder.
 */
export function SidebarPlanWidget({ collapsed, onNavigateBilling }: SidebarPlanWidgetProps) {
  const subscription = usePlanStore(s => s.subscription);
  const usage = usePlanStore(s => s.usage);

  if (!subscription) return null;

  const planLabel = subscription.isLifetime ? 'Lifetime' : subscription.planName;
  const credits = usage?.aiCredits;
  const percent = credits && !credits.unlimited ? Math.min(100, Math.round(credits.percent)) : null;
  const severity = usage?.tip?.severity;
  const warn = severity === 'warn' || severity === 'blocked';
  const handleClick = () => {
    trackUpgradeCta('sidebar_plan_widget', warn ? 'credits' : 'organic');
    onNavigateBilling();
  };

  // Collapsed rail is icon-width — show just the badge, color-cued when at risk.
  if (collapsed) {
    return (
      <div className="flex justify-center px-2 pt-2">
        <button
          onClick={handleClick}
          title={`${planLabel}${percent !== null ? ` · ${percent}% credits used` : ''}`}
          className={`flex size-8 items-center justify-center rounded-md text-xs font-semibold transition-colors ${
            warn
              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
              : 'bg-[#F3F4F6] text-[#6B7280] hover:bg-[#E5E7EB]'
          }`}
        >
          {warn ? <AlertTriangle className="size-4" /> : planLabel.slice(0, 1).toUpperCase()}
        </button>
      </div>
    );
  }

  return (
    <div className="px-2 pt-2">
      <button onClick={handleClick} className="group w-full rounded-md px-2.5 py-1.5 text-left">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2 text-sm text-[#6B7280] transition-colors group-hover:text-purple-600">
            <Zap className="size-4 flex-shrink-0" />
            AI credits
          </span>
          <Badge
            variant="secondary"
            className="bg-gradient-to-r from-purple-100 to-pink-100 px-1.5 py-0 text-[11px] text-purple-700"
          >
            {planLabel}
          </Badge>
        </div>

        {percent !== null ? (
          <div className="mt-1.5 flex items-center gap-2">
            <div className="h-1 flex-1 overflow-hidden rounded-full bg-[#E5E7EB]">
              <div
                className={`h-full rounded-full transition-all ${
                  warn
                    ? 'bg-gradient-to-r from-amber-400 to-orange-500'
                    : 'bg-gradient-to-r from-purple-500 to-pink-500'
                }`}
                style={{ width: `${percent}%` }}
              />
            </div>
            <span className="text-[11px] text-[#6B7280] transition-colors group-hover:text-purple-600">
              {percent}%
            </span>
          </div>
        ) : (
          credits?.unlimited && <p className="mt-0.5 text-[11px] text-[#6B7280]">Unlimited</p>
        )}
      </button>

      {warn && usage?.tip?.message && (
        <div className="mt-1.5 flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800">
          <AlertTriangle className="mt-0.5 size-3.5 flex-shrink-0 text-amber-600" />
          <span className="leading-snug">{usage.tip.message}</span>
        </div>
      )}
    </div>
  );
}
